import React, { useEffect, useState } from 'react';
import { Link, NavLink, useLocation } from 'react-router-dom';
import logo from '../../assets/images/logo.png';
import './HeaderHover.css';

const Header = () => {
  const location = useLocation();
  const currentPath = location.pathname;

  const [openMobileDropdown, setOpenMobileDropdown] = useState(null);

  // Set active nav item based on path
  useEffect(() => {
    const dropdownItems = document.querySelectorAll('.dropdown-item');
    dropdownItems.forEach((item) => item.classList.remove('active'));
    document.querySelectorAll('.nav-item').forEach((item) => item.classList.remove('active'));

    let activeItemFound = false;

    dropdownItems.forEach((item) => {
      const linkPath = item.getAttribute('href') || item.querySelector('a')?.getAttribute('href');
      if (linkPath && currentPath === linkPath) {
        item.classList.add('active');
        activeItemFound = true;
        const parentDropdown = item.closest('.dropdown');
        if (parentDropdown) {
          parentDropdown.classList.add('active');
        }
      }
    });

    if (!activeItemFound) {
      document.querySelectorAll('.nav-link').forEach((navLink) => {
        if (navLink.getAttribute('href') === currentPath) {
          const parentItem = navLink.closest('.nav-item');
          if (parentItem) {
            parentItem.classList.add('active');
          }
        }
      });
    }
  }, [currentPath]);

  // Prevent background scroll when mobile menu is open
  useEffect(() => {
    const navbarCollapse = document.getElementById('navbarNav');
    const observer = new MutationObserver((mutations) => {
      mutations.forEach((mutation) => {
        if (mutation.attributeName === 'class') {
          if (navbarCollapse.classList.contains('show')) {
            document.body.classList.add('menu-open');
          } else {
            document.body.classList.remove('menu-open');
            setOpenMobileDropdown(null); // close any open dropdown
          }
        }
      });
    });
    observer.observe(navbarCollapse, { attributes: true });
    return () => observer.disconnect();
  }, []);

  const closeMobileMenu = () => {
    const navbarCollapse = document.getElementById('navbarNav');
    if (navbarCollapse?.classList.contains('show')) {
      const navbarToggler = document.querySelector('.navbar-toggler');
      if (navbarToggler) navbarToggler.click();
    }
  };

  const handleMobileDropdownToggle = (key) => {
    setOpenMobileDropdown((prev) => (prev === key ? null : key));
  };

  return (
    <nav className="navbar navbar-expand-lg py-3 sticky-top">
      <div className="container">
        <Link className="navbar-brand d-flex align-items-center" to="/">
          <img src={logo} className="logo me-2" alt="Logo" />
        </Link>

        <button className="navbar-toggler" type="button" data-bs-toggle="collapse" data-bs-target="#navbarNav">
          <span className="navbar-toggler-icon"></span>
        </button>

        <div className="collapse navbar-collapse" id="navbarNav">
          <button className="mobile-menu-close d-lg-none" onClick={closeMobileMenu}>
            <i className="bi bi-x"></i>
          </button>
          <ul className="navbar-nav gap-4 ms-0">
            <li className="nav-item">
              <NavLink className="nav-link fw-medium" to="/" onClick={closeMobileMenu}>Home</NavLink>
            </li>

            {/* Dropdown: Platform */}
            <li className="nav-item dropdown">
              <a
                href="#"
                className="nav-link fw-medium dropdown-toggle"
                aria-expanded={openMobileDropdown === 'platform'}
                onClick={(e) => {
                  e.preventDefault();
                  if (window.innerWidth < 992) handleMobileDropdownToggle('platform');
                }}
              >
                Platform
              </a>
              <div className={`dropdown-menu products-dropdown ${openMobileDropdown === 'platform' ? 'show' : ''}`}>
                <div className="container p-4">
                  <div className="row"><h6 className="dropdown-header">Platform Features</h6></div>
                  <div className="row g-4">
                    <div className="col-md-4">
                      <Link className="dropdown-item product-item" to="/platform/website-ai-chatbot" onClick={closeMobileMenu}>
                        <div className="product-info"><h6>Website AI Chatbot</h6><p>Smart website assistant</p></div>
                      </Link>
                    </div>
                    <div className="col-md-4">
                      <Link className="dropdown-item product-item" to="/platform/whatsapp-integration" onClick={closeMobileMenu}>
                        <div className="product-info"><h6>WhatsApp Integration</h6><p>WhatsApp messaging automation</p></div>
                      </Link>
                    </div>
                  </div>
                </div>
              </div>
            </li>

            {/* Dropdown: Features */}
            <li className="nav-item dropdown">
              <a
                href="#"
                className="nav-link fw-medium dropdown-toggle"
                aria-expanded={openMobileDropdown === 'features'}
                onClick={(e) => {
                  e.preventDefault();
                  if (window.innerWidth < 992) handleMobileDropdownToggle('features');
                }}
              >
                Features
              </a>
              <div className={`dropdown-menu products-dropdown ${openMobileDropdown === 'features' ? 'show' : ''}`}>
                <div className="container p-4">
                  <div className="row"><h6 className="dropdown-header">Key Features</h6></div>
                  <div className="row g-4">
                    <div className="col-md-4">
                      <Link className="dropdown-item product-item" to="/features/lead-generation" onClick={closeMobileMenu}>
                        <div className="product-info"><h6>Lead Generation</h6><p>Convert visitors into customers</p></div>
                      </Link>
                    </div>
                    <div className="col-md-4">
                      <Link className="dropdown-item product-item" to="/features/support-automation" onClick={closeMobileMenu}>
                        <div className="product-info"><h6>Support Automation</h6><p>24/7 automated customer support</p></div>
                      </Link>
                    </div>
                    <div className="col-md-4">
                      <Link className="dropdown-item product-item" to="/features/ai-campaigns" onClick={closeMobileMenu}>
                        <div className="product-info"><h6>AI Campaigns</h6><p>Intelligent marketing automation</p></div>
                      </Link>
                    </div>
                  </div>
                </div>
              </div>
            </li>

            {/* Dropdown: Solutions */}
            <li className="nav-item dropdown">
              <a
                href="#"
                className="nav-link fw-medium dropdown-toggle"
                aria-expanded={openMobileDropdown === 'solutions'}
                onClick={(e) => {
                  e.preventDefault();
                  if (window.innerWidth < 992) handleMobileDropdownToggle('solutions');
                }}
              >
                Solutions
              </a>
              <div className={`dropdown-menu products-dropdown ${openMobileDropdown === 'solutions' ? 'show' : ''}`}>
                <div className="container p-4">
                  <div className="row"><h6 className="dropdown-header">By Industry</h6></div>
                  <div className="row g-4">
                    <div className="col-md-4">
                      <Link className="dropdown-item product-item" to="/industries/b2c-d2c" onClick={closeMobileMenu}>
                        <div className="product-info"><h6>B2C & D2C Brands</h6><p>Engage customers across channels</p></div>
                      </Link>
                    </div>
                    <div className="col-md-4">
                      <Link className="dropdown-item product-item" to="/industries/hospitality" onClick={closeMobileMenu}>
                        <div className="product-info"><h6>Hospitality & Resorts</h6><p>Enhance guest experiences</p></div>
                      </Link>
                    </div>
                    <div className="col-md-4">
                      <Link className="dropdown-item product-item" to="/industries/education" onClick={closeMobileMenu}>
                        <div className="product-info"><h6>Education & EdTech</h6><p>Support students and staff</p></div>
                      </Link>
                    </div>
                  </div>
                </div>
              </div>
            </li>

            {/* Non-dropdown links */}
            <li className="nav-item">
              <NavLink className="nav-link fw-medium" to="/about" onClick={closeMobileMenu}>About</NavLink>
            </li>
            <li className="nav-item">
              <NavLink className="nav-link fw-medium" to="/pricing" onClick={closeMobileMenu}>Pricing</NavLink>
            </li>
            <li className="nav-item">
              <a className="nav-link fw-medium" href="https://blog.aichatassist.com" target="_blank" rel="noopener noreferrer" onClick={closeMobileMenu}>Blog</a>
            </li>
            <li className="nav-item">
              <NavLink className="nav-link fw-medium" to="/contact" onClick={closeMobileMenu}>Contact</NavLink>
            </li>
          </ul>

          {/* Mobile Auth Buttons */}
          <div className="d-lg-none mt-4 pt-3 border-top">
            <div className="d-flex flex-column gap-2">
              <a href="https://app.aichatassist.com/auth/sign-in" className="btn btn-outline-primary px-4 py-2 rounded-pill w-100" onClick={closeMobileMenu}>Login</a>
              <a href="https://app.aichatassist.com/auth/sign-up" className="btn btn-primary px-4 py-2 rounded-pill w-100" onClick={closeMobileMenu}>Get Started <i className="bi bi-arrow-right"></i></a>
            </div>
          </div>
        </div>

        {/* Desktop Auth Buttons */}
        <div className="d-flex gap-3 align-items-center">
          <a href="https://app.aichatassist.com/auth/sign-in" className="btn btn-outline-primary px-4 py-2 rounded-pill navbar btn-primary">Login</a>
          <a href="https://app.aichatassist.com/auth/sign-up" className="btn btn-primary px-4 py-2 rounded-pill">Get Started <i className="bi bi-arrow-right"></i></a>
        </div>
      </div>
    </nav>
  );
};

export default Header;
